import { Folder } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { To } from 'react-router-dom';
import { Typography } from '~/components/Typography';
import { CardActionsTitleText } from '~/components/cards';
import {
  FolderChildren,
  FolderContainer,
  FolderIconContainer,
  FolderLink,
  FolderMetaContainer,
  FolderNotLink,
} from './styles';

type OptionalLinkProps = Pick<FolderCardProps, 'children' | 'to'>;

const OptionalLink = ({ children, to }: OptionalLinkProps) =>
  to ? <FolderLink to={to}>{children}</FolderLink> : <FolderNotLink>{children}</FolderNotLink>;

export interface FolderCardFolder {
  id: number;
  name: string;
  childrenCount: number;
  itemCount: number;
}

export interface FolderCardProps {
  children?: ReactNode;
  folder: FolderCardFolder;
  to?: To;
  label?: string;
}

export const FolderCard = ({ children, folder, to, label = 'item' }: FolderCardProps) => {
  const { t } = useTranslation();

  return (
    <FolderContainer>
      <OptionalLink to={to}>
        <FolderIconContainer>
          <Folder color="primary" sx={{ height: '100%', width: '100%' }} />
        </FolderIconContainer>
        <FolderMetaContainer>
          <CardActionsTitleText title={folder.name} />
          <Typography color="text.secondary" variant="subtitle2">
            {folder.childrenCount > 0 &&
              `${folder.childrenCount} ${t(`common.pluralize.folder_plural`, {
                count: folder.childrenCount,
              })}`}
            {folder.childrenCount > 0 ? ', ' : ''}
            {folder.itemCount} {t(`common.pluralize.${label}_plural`, { count: folder.itemCount })}
          </Typography>
        </FolderMetaContainer>
      </OptionalLink>
      <FolderChildren>{children}</FolderChildren>
    </FolderContainer>
  );
};
